import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button as ChakraUiButton,
  HStack,
  NativeSelect,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useProfile, useTransactions, useWallets } from 'hooks';
import { useTranslation } from 'localization';
import { colors } from 'theme';
import { Transaction } from 'types';

import {
  Button,
  DateRangeSelect,
  Spinner,
  TransactionDetailModal,
  TransactionListItem,
} from 'components';
import { exportPDF } from 'utils';

const transactionsFilterSelectCss = {
  colorScheme: 'dark',
  cursor: 'pointer',
  '& > option, & > optgroup': {
    backgroundColor: `${colors.dark} !important`,
    color: `${colors.body} !important`,
  },
};

const TRansactionsTab: React.FC = () => {
  const { t } = useTranslation('component.wallet.tabs.transactions');
  const [pagination, setPagination] = React.useState<{
    current: number;
    pageSize: number;
  }>({
    current: 1,
    pageSize: 20,
  });

  const [selectedTr, setSelectedTr] = React.useState<Transaction | undefined>(
    undefined
  );
  const [dateRange, setDateRange] = React.useState<(Date | null)[]>([
    null,
    null,
  ]);

  const [searchparams, setSearchparams] = useSearchParams();
  const { open, onOpen, onClose } = useDisclosure();

  const selectedWallet = searchparams.get('wallet') || '';
  const selectedStatus = (searchparams.get('status') ||
    '') as Transaction['status'];

  const dateInQuery = dateRange.includes(null)
    ? {}
    : {
        from_date: dayjs(dateRange[0]).toISOString(),
        to_date: dayjs(dateRange[1]).toISOString(),
      };
  const walletInQuery = searchparams.get('wallet')
    ? {
        currency: searchparams.get('wallet'),
      }
    : {};

  const { data: profile } = useProfile();
  const {
    data: transactions,
    isFetched: transactionsFetched,
    isLoading,
  } = useTransactions({
    queries: selectedStatus
      ? {
          ...pagination,
          ...dateInQuery,
          ...walletInQuery,
          status: selectedStatus,
        }
      : { ...pagination, ...dateInQuery, ...walletInQuery },
  });

  const { data: wallets, isFetched } = useWallets({ queries: {} });

  const transactionTotal = transactions?.pagination?.total ?? 0;
  const transactionTotalPages = Math.ceil(
    transactionTotal / pagination.pageSize
  );

  const resetPagination = () => {
    setPagination({ current: 1, pageSize: 20 });
  };

  return (
    <VStack mx={{ base: 4, lg: 0 }}>
      <Box
        w="100%"
        py={6}
        display="flex"
        flexDir={{ base: 'column', lg: 'row' }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Text
          fontWeight="500"
          fontSize="var(--chakra-font-sizes-4xl)"
          color="body"
        >
          {t('list')}
        </Text>
      </Box>
      <VStack w="100%">
        <Stack
          flexDir={{ base: 'column', md: 'row' }}
          w="100%"
          justify={{ base: 'center', md: 'space-between' }}
        >
          <Button
            variant="ghost"
            title={t('downloadExcel')}
            onClick={() => {
              if (transactions?.list && transactions.list.length > 0) {
                const exportData = transactions.list.map((item) => {
                  const isDestination =
                    typeof item.destination_wallet === 'object' &&
                    item.destination_wallet !== null;
                  const title = isDestination
                    ? item.destination_wallet.currency.title
                    : item.source_wallet.currency.title;
                  const amount = isDestination
                    ? item.destination_amount
                    : item.amount;

                  const brokerData =
                    profile?.role.title === 'BROKER'
                      ? {
                          id: item.id,
                          status: item.status,
                          created_at: item.created_at,
                          updated_at: item.updated_at,
                          'broker_data:destination_amount':
                            item.destination_amount,
                          'broker_data:fee': item.fee.fee_amount,
                          'broker_data:currency':
                            item.form.deposit_broker?.currency ||
                            item.form.withdraw_broker?.currency,
                          'broker_data:trading_account':
                            item.form.deposit_broker?.trading_account ||
                            item.form.withdraw_broker?.trading_account,
                          'broker_data:payment_id':
                            item.form.deposit_broker?.payment_id ||
                            item.form.withdraw_broker?.payment_id,
                          'broker_data:client_name':
                            item.form.deposit_broker?.client_name ||
                            item.form.withdraw_broker?.client_name,
                          'broker_data:client_email':
                            item.form.deposit_broker?.client_email ||
                            item.form.withdraw_broker?.client_email,
                          'broker_data:suggested_memo':
                            item.form.deposit_broker?.suggested_memo ||
                            item.form.withdraw_broker?.suggested_memo,
                          'broker_data:magix_id':
                            item.form.deposit_broker?.magix_id ||
                            item.form.withdraw_broker?.magix_id,
                          //
                          'broker_data:payer_name':
                            item.form.deposit_broker?.payer_name || '-',
                          'broker_data:payee_account':
                            item.form.withdraw_broker?.payee_account || '-',
                          'broker_data:payee_name':
                            item.form.withdraw_broker?.payee_name || '-',
                        }
                      : {};
                  return {
                    type:
                      item.transaction_method.type === 'DEPOSIT'
                        ? profile?.role.title === 'LD'
                          ? 'Withdraw'
                          : 'Deposit'
                        : item.transaction_method.type === 'WITHDRAW'
                          ? profile?.role.title === 'LD'
                            ? 'Deposit'
                            : 'Withdraw'
                          : item.transaction_method.type === 'SWAP'
                            ? 'SWAP'
                            : 'Exchange',
                    title: title,
                    amount: amount,
                    time: item.created_at,
                    ...brokerData,
                  };
                });
                exportPDF({ data: exportData, fileName: 'transactions' });
              }
            }}
          />
          <Stack
            w={{ base: '100%', md: 'max-content' }}
            align="center"
            justify="center"
          >
            <Stack
              flexDir={{ base: 'column', xl: 'row' }}
              align="center"
              justify="space-between"
              w="max-content"
            >
              <Stack
                w="100%"
                flexDir="row"
                align="center"
                justifyContent="space-between"
              >
                {isFetched && wallets?.list && wallets?.list.length > 0 && (
                  <Box>
                    <NativeSelect.Root w={{ base: '100%', sm: 'auto' }}>
                      <NativeSelect.Field
                        value={selectedWallet}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          searchparams.set('wallet', e.target.value);
                          resetPagination();
                          setSearchparams(searchparams);
                        }}
                        bg="dark"
                        color="body"
                        borderWidth="1px"
                        borderColor="body2"
                        borderRadius="lg"
                        fontSize="sm"
                        fontWeight="normal"
                        h="40px"
                        minW={{ base: '100%', sm: '7.5rem' }}
                        px={3}
                        _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
                        _focus={{
                          borderColor: 'primary',
                          boxShadow: 'none',
                        }}
                        css={transactionsFilterSelectCss}
                      >
                        <option value="">All</option>
                        {wallets?.list.map((wallet) => {
                          return (
                            <option
                              key={wallet.currency.id}
                              value={wallet.currency.id}
                            >
                              {wallet.currency.symbol}
                            </option>
                          );
                        })}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator color="body2" />
                    </NativeSelect.Root>
                  </Box>
                )}
                <Box>
                  <NativeSelect.Root w={{ base: '100%', sm: 'auto' }}>
                    <NativeSelect.Field
                      value={selectedStatus}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        searchparams.set('status', e.target.value);
                        resetPagination();
                        setSearchparams(searchparams);
                      }}
                      bg="dark"
                      color="body"
                      borderWidth="1px"
                      borderColor="body2"
                      borderRadius="lg"
                      fontSize="sm"
                      fontWeight="normal"
                      h="40px"
                      minW={{ base: '100%', sm: '7.5rem' }}
                      px={3}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
                      _focus={{
                        borderColor: 'primary',
                        boxShadow: 'none',
                      }}
                      css={transactionsFilterSelectCss}
                    >
                      <option value="">All</option>
                      <option value="SETTLED">Settled</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator color="body2" />
                  </NativeSelect.Root>
                </Box>
              </Stack>
              <Box>
                <DateRangeSelect
                  dateRange={dateRange}
                  setDateRange={(v) => {
                    resetPagination();
                    setDateRange(v);
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        {isLoading && (
          <HStack justify="center" w="100%">
            <Spinner />
          </HStack>
        )}
        {transactions && transactions.list?.length === 0 ? (
          <VStack w="100%" m="2" textAlign="center">
            <Text color="body2">{t('noTransaction')}</Text>
          </VStack>
        ) : (
          <Box
            w="100%"
            borderTopWidth="1px"
            borderTopColor="rgba(255,255,255,0.08)"
          >
            {transactions?.list.map((transaction) => (
              <TransactionListItem
                key={transaction.id}
                transaction={transaction}
                profile={profile}
                onClick={() => {
                  setSelectedTr(transaction);
                  onOpen();
                }}
              />
            ))}
          </Box>
        )}
        {transactionTotal > pagination.pageSize && transactionsFetched && (
          <HStack justify="center" wrap="wrap" gap={1} py={4}>
            <ChakraUiButton
              size="sm"
              variant="ghost"
              color="body"
              disabled={pagination.current <= 1}
              onClick={() =>
                setPagination((p) => ({
                  ...p,
                  current: Math.max(1, p.current - 1),
                }))
              }
            >
              ‹
            </ChakraUiButton>
            {Array.from({ length: transactionTotalPages }, (_, i) => i + 1).map(
              (page) => (
                <ChakraUiButton
                  key={page}
                  size="sm"
                  variant="ghost"
                  color="body"
                  bg={pagination.current === page ? 'primary' : 'transparent'}
                  _hover={{
                    bg:
                      pagination.current === page
                        ? 'primary'
                        : 'rgba(255, 255, 255, 0.08)',
                  }}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, current: page }))
                  }
                >
                  {page}
                </ChakraUiButton>
              )
            )}
            <ChakraUiButton
              size="sm"
              variant="ghost"
              color="body"
              disabled={pagination.current >= transactionTotalPages}
              onClick={() =>
                setPagination((p) => ({
                  ...p,
                  current: Math.min(transactionTotalPages, p.current + 1),
                }))
              }
            >
              ›
            </ChakraUiButton>
          </HStack>
        )}
        {profile && selectedTr && (
          <TransactionDetailModal
            isOpen={open}
            onClose={onClose}
            transaction={selectedTr}
            profile={profile}
          />
        )}
      </VStack>
    </VStack>
  );
};

export default TRansactionsTab;
